import { io, Socket } from 'socket.io-client';
import { Conversation, ConversationSocket } from '~api';
import { logger } from '~portal/lib';

type SocketConfig = {
  /** Backend origin (VITE_BACKEND_API_URL) — socket.io connects on its default path. */
  url: string;
  /** Resolved per connection attempt so reconnects pick up refreshed tokens. */
  getToken: () => Promise<string>;
};

type SocketHandlers = {
  onEvent: (conversationId: string, event: Conversation.ClientEvent<Date, string>) => void;
  onError: (conversationId: string, message: string) => void;
  /** Fired after an automatic reconnect: replay these conversations from the server. */
  onReconnect: (activeConversationIds: string[]) => void;
};

/**
 * @desc Socket payloads cross as JSON; revive the event timestamp into a real Date. Exported for
 * `socket-wire.test.ts`, which round-trips a backend-serialised event through it — the decode
 * half of the wire contract has no other seam to test at.
 */
export const reviveEvent = (
  event: Conversation.ClientEvent<Date, string>,
): Conversation.ClientEvent<Date, string> => ({
  ...event,
  timestamp: new Date(event.timestamp as unknown as string),
});

/**
 * @desc View-independent owner of the conversation event socket (H2-172). One connection per
 * app instance, held open exactly while at least one conversation expects backend updates —
 * navigating between routes never touches it, and state lands in the Zustand store regardless
 * of which view (if any) is mounted. `configure()` is wired once by the SocketBridge inside the
 * ApiProvider tree; everything else is driven by ChatAgent/useConversation.
 */
type ConversationSocketClient = Socket<
  ConversationSocket.ServerToClient,
  ConversationSocket.ClientToServer
>;

class SocketManagerImpl {
  private socket?: ConversationSocketClient;
  private config?: SocketConfig;
  private handlers?: SocketHandlers;
  /** @desc Conversations currently expecting backend updates — the connection's refcount. */
  private readonly active = new Set<string>();

  configure(config: SocketConfig, handlers: SocketHandlers) {
    this.config = config;
    this.handlers = handlers;
    // A conversation may have been acquired before configuration landed (first render races).
    if (this.active.size > 0) this.connect();
  }

  /** @desc Mark a conversation as expecting updates; opens the connection when needed. */
  acquire(conversationId: string) {
    this.active.add(conversationId);
    this.connect();
  }

  /**
   * @desc A turn finished (or was never running): drop the conversation from the refcount and
   * close the connection once nothing is left expecting updates.
   */
  release(conversationId: string) {
    this.active.delete(conversationId);
    if (this.active.size === 0) this.disconnect();
  }

  /** @desc Re-home a refcount entry once a `new` conversation learns its real id. */
  rekey(from: string, to: string) {
    if (!this.active.has(from)) return;
    this.active.delete(from);
    this.active.add(to);
  }

  /**
   * @desc Ask the server whether a run is in flight for the conversation, holding the
   * connection open while it is. Resolves false (and releases) when nothing is running.
   */
  async watch(conversationId: string): Promise<boolean> {
    this.acquire(conversationId);
    const socket = this.socket;
    if (!socket) {
      this.release(conversationId);
      return false;
    }
    try {
      const { active } = await socket
        .timeout(10_000)
        .emitWithAck('conversation:watch', { conversationId });
      if (!active) this.release(conversationId);
      return active;
    } catch (err) {
      logger.warn('[SocketManager] watch ack failed', { conversationId, error: String(err) });
      this.release(conversationId);
      return false;
    }
  }

  private connect() {
    if (this.socket || !this.config || !this.handlers) return;
    const handlers = this.handlers;

    const socket: ConversationSocketClient = io(this.config.url, {
      withCredentials: true,
      transports: ['websocket'],
      auth: (cb) => {
        this.config!.getToken()
          .then((token) => cb({ token }))
          .catch((err: unknown) => {
            logger.error('[SocketManager] token resolution failed', { error: String(err) });
            cb({});
          });
      },
    });

    socket.on('conversation:event', ({ conversationId, event }) => {
      handlers.onEvent(conversationId, reviveEvent(event));
    });
    socket.on('conversation:error', ({ conversationId, message }) => {
      handlers.onError(conversationId, message);
    });
    socket.on('connect_error', (err) => {
      logger.warn('[SocketManager] connection error', { error: err.message });
    });
    // socket.io reconnects automatically; a gap may have dropped events, so the bridge replays
    // every still-active conversation from the source of truth.
    socket.io.on('reconnect', () => {
      logger.info('[SocketManager] reconnected', { active: this.active.size });
      handlers.onReconnect(Array.from(this.active));
    });

    this.socket = socket;
  }

  private disconnect() {
    this.socket?.disconnect();
    this.socket = undefined;
  }
}

export const SocketManager = new SocketManagerImpl();
